/**
 * Platform detection utilities for keyboard shortcut display.
 *
 * On macOS the modifier keys use different symbols:
 *   Ctrl  → ⌘ (Cmd)
 *   Alt   → ⌥ (Option)
 *
 * This module provides helpers so that the UI always shows the correct
 * modifier for the user's platform.
 */

/**
 * Returns true when the user agent is running on macOS.
 * Safe for SSR (returns false when `navigator` is unavailable).
 */
export const isMacPlatform = () => {
  if (typeof navigator === "undefined") return false;
  // navigator.userAgentData is the modern API; fall back to userAgent string.
  if (navigator.userAgentData) {
    return navigator.userAgentData.platform === "macOS";
  }
  return /Mac|iPhone|iPad|iPod/i.test(navigator.platform || navigator.userAgent);
};

/**
 * Return the correct modifier label for "Ctrl" shortcuts.
 * macOS → "⌘"  |  Windows/Linux → "Ctrl"
 */
export const getCtrlLabel = () => (isMacPlatform() ? "⌘" : "Ctrl");

/**
 * Return the correct modifier label for "Alt" shortcuts.
 * macOS → "⌥"  |  Windows/Linux → "Alt"
 */
export const getAltLabel = () => (isMacPlatform() ? "⌥" : "Alt");

/**
 * Build a formatted shortcut string, e.g. "⌘ + Z" or "Ctrl + Z".
 * @param {"ctrl"|"alt"|"shift"|null} modifier
 * @param {string} key - The key to display (e.g. "Z", "H", "+", "-")
 */
export const formatShortcut = (modifier, key) => {
  if (!modifier) return key;
  const labels = { ctrl: getCtrlLabel(), alt: getAltLabel(), shift: "Shift" };
  return `${labels[modifier] || modifier} + ${key}`;
};
